/* LookalikeModal — vind LinkedIn-profielen die lijken op een referentieprofiel.
 *
 * Twee instappunten:
 *  - Talent Search-pagina: gebruiker plakt een LinkedIn-URL (Apify-scrape)
 *  - Talent Pool-kaart:    `candidate` prop is prefilled (geen scrape-kosten)
 *
 * Resultaten worden eerst gereviewd; per profiel "Voeg toe aan pool".
 */
import React, { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import i18n from "i18next";
import {
    X, Loader2, UserSearch, Linkedin, Plus, Check, ExternalLink, Sparkles,
} from "lucide-react";

export function LookalikeModal({ open, onClose, candidate = null }) {
    const [url, setUrl] = useState("");
    const [geo, setGeo] = useState("");
    const [limit, setLimit] = useState(3);
    const [searching, setSearching] = useState(false);
    const [data, setData] = useState(null);       // { reference, results, source }
    const [addedIds, setAddedIds] = useState(new Set());  // linkedin_urls added this session
    const [addingUrl, setAddingUrl] = useState(null);

    if (!open) return null;

    const runSearch = async () => {
        setSearching(true);
        setData(null);
        try {
            const body = candidate
                ? { candidate_id: candidate.id, limit }
                : { linkedin_url: url.trim(), limit };
            if (geo.trim()) body.location = geo.trim();
            const r = await api.post("/lookalike/search", body);
            setData(r.data);
            if ((r.data.results || []).length === 0) {
                toast.info(r.data.geo_filtered > 0
                    ? i18n.t("lookalike.no_results_geo", { n: r.data.geo_filtered, geo: r.data.geo })
                    : i18n.t("lookalike.no_results"));
            }
        } catch (e) {
            toast.error(e?.response?.data?.detail || i18n.t("lookalike.search_failed"));
        } finally { setSearching(false); }
    };

    const addToPool = async (profile) => {
        setAddingUrl(profile.linkedin_url || profile.name);
        try {
            await api.post("/lookalike/add-to-pool", {
                profile,
                reference_name: data?.reference?.name || "",
                pool: candidate?.talent_pool || "external",
            });
            setAddedIds((prev) => new Set(prev).add(profile.linkedin_url || profile.name));
            toast.success(i18n.t("lookalike.added_ok", { name: profile.name }));
        } catch (e) {
            toast.error(e?.response?.data?.detail || i18n.t("lookalike.add_failed"));
        } finally { setAddingUrl(null); }
    };

    const scoreColor = (s) => s >= 70 ? "#22c55e" : s >= 40 ? "var(--jr-amber)" : "#f87171";

    return (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm grid place-items-center p-4"
            data-testid="lookalike-modal"
            onClick={searching ? undefined : onClose}>
            <div className="w-full max-w-2xl max-h-[85vh] overflow-y-auto rounded-xl border border-white/10 bg-[var(--jr-surface)] p-6"
                onClick={(e) => e.stopPropagation()}>
                {/* Header */}
                <div className="flex items-start justify-between gap-3 mb-4">
                    <div>
                        <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                            <UserSearch size={18} className="text-[#a78bfa]" /> {i18n.t("lookalike.title")}
                        </h3>
                        <p className="text-xs text-[var(--jr-text-2)] mt-1">
                            {candidate
                                ? i18n.t("lookalike.subtitle_candidate", { name: candidate.name })
                                : i18n.t("lookalike.subtitle_url")}
                        </p>
                    </div>
                    <button onClick={onClose} data-testid="lookalike-close"
                        className="p-1.5 rounded-full text-[var(--jr-text-3)] hover:text-white hover:bg-white/5">
                        <X size={16} />
                    </button>
                </div>

                {/* Referentie-input */}
                {!candidate && (
                    <input value={url} onChange={(e) => setUrl(e.target.value)}
                        placeholder="https://www.linkedin.com/in/..."
                        data-testid="lookalike-url-input"
                        className="w-full bg-[var(--jr-surface-2)] border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white outline-none focus:border-[#a78bfa] mb-3" />
                )}
                {candidate && (
                    <div className="rounded-lg border border-white/10 bg-[var(--jr-surface-2)] px-3 py-2.5 mb-3 text-sm"
                        data-testid="lookalike-reference">
                        <span className="text-white font-medium">{candidate.name}</span>
                        <span className="text-[var(--jr-text-2)]"> · {candidate.title}</span>
                        {(candidate.skills || []).length > 0 && (
                            <div className="mt-1.5 flex flex-wrap gap-1">
                                {candidate.skills.slice(0, 8).map((s, i) => (
                                    <span key={i} className="text-[10px] px-1.5 py-0.5 rounded bg-white/5 border border-white/10 text-[var(--jr-text-2)]">{s}</span>
                                ))}
                            </div>
                        )}
                    </div>
                )}

                {/* Aantal + geo + zoekknop */}
                <div className="flex items-center gap-3 flex-wrap">
                    <label className="text-xs text-[var(--jr-text-2)] flex items-center gap-2">
                        {i18n.t("lookalike.limit_label")}
                        <input type="number" min={1} max={10} value={limit}
                            onChange={(e) => setLimit(Math.max(1, Math.min(10, parseInt(e.target.value || "3", 10))))}
                            data-testid="lookalike-limit"
                            className="w-16 bg-[var(--jr-surface-2)] border border-white/10 rounded px-2 py-1.5 text-sm text-white outline-none focus:border-[#a78bfa]" />
                    </label>
                    <label className="text-xs text-[var(--jr-text-2)] flex items-center gap-2 flex-1 min-w-[200px]">
                        {i18n.t("lookalike.geo_label")}
                        <input value={geo} onChange={(e) => setGeo(e.target.value)}
                            placeholder={i18n.t("lookalike.geo_placeholder")}
                            data-testid="lookalike-geo"
                            className="flex-1 bg-[var(--jr-surface-2)] border border-white/10 rounded px-2 py-1.5 text-sm text-white outline-none focus:border-[#a78bfa]" />
                    </label>
                    <button onClick={runSearch}
                        disabled={searching || (!candidate && !url.trim())}
                        data-testid="lookalike-run"
                        className="ml-auto inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#a78bfa] text-black text-sm font-semibold hover:bg-[#a78bfa]/85 disabled:opacity-50">
                        {searching ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
                        {searching ? i18n.t("lookalike.searching") : i18n.t("lookalike.search_btn")}
                    </button>
                </div>
                <p className="mt-2 text-[10px] text-[var(--jr-text-3)]">
                    {i18n.t("lookalike.geo_hint")}{!candidate ? ` · ${i18n.t("lookalike.cost_hint")}` : ""}
                </p>

                {/* Resultaten */}
                {data && (
                    <div className="mt-5" data-testid="lookalike-results">
                        <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-[var(--jr-text-3)] mb-2">
                            {i18n.t("lookalike.results_label", { count: (data.results || []).length })}
                            {data.source && <span className="ml-2 normal-case tracking-normal">via {data.source === "perplexity" ? "Perplexity live web-search" : "Apollo"}</span>}
                            {data.geo && (
                                <span className="ml-2 normal-case tracking-normal text-[#a78bfa]" data-testid="lookalike-geo-chip">
                                    · {i18n.t("lookalike.geo_active", { geo: data.geo })}
                                    {data.geo_filtered > 0 ? ` (${i18n.t("lookalike.geo_filtered", { n: data.geo_filtered })})` : ""}
                                </span>
                            )}
                        </div>
                        {(data.errors || []).length > 0 && (
                            <div className="mb-2 text-[10px] text-[var(--jr-amber)] font-mono" data-testid="lookalike-errors">
                                {data.errors.map((e, i) => <div key={i}>⚠ {e}</div>)}
                            </div>
                        )}
                        <div className="space-y-2">
                            {(data.results || []).map((r, idx) => {
                                const isAdded = r.already_in_pool || addedIds.has(r.linkedin_url || r.name);
                                return (
                                    <div key={idx} className="rounded-lg border border-white/10 bg-[var(--jr-surface-2)] p-3"
                                        data-testid={`lookalike-result-${idx}`}>
                                        <div className="flex items-start justify-between gap-3">
                                            <div className="min-w-0">
                                                <div className="flex items-center gap-2 flex-wrap">
                                                    <span className="text-sm font-medium text-white">{r.name}</span>
                                                    <span className="text-[10px] font-mono px-1.5 py-0.5 rounded-full border"
                                                        style={{ color: scoreColor(r.match_score), borderColor: scoreColor(r.match_score) + "55", background: scoreColor(r.match_score) + "14" }}
                                                        data-testid={`lookalike-score-${idx}`}>
                                                        {i18n.t("lookalike.match")} {r.match_score}%
                                                    </span>
                                                    {r.linkedin_url && (
                                                        <a href={r.linkedin_url} target="_blank" rel="noreferrer"
                                                            className="text-[#4d9be6] hover:underline inline-flex items-center gap-0.5 text-[11px]"
                                                            data-testid={`lookalike-linkedin-${idx}`}>
                                                            <Linkedin size={11} /> <ExternalLink size={9} />
                                                        </a>
                                                    )}
                                                </div>
                                                <div className="text-xs text-[var(--jr-text-2)] truncate mt-0.5">
                                                    {[r.title, r.company, r.location].filter(Boolean).join(" · ")}
                                                </div>
                                                {(r.shared_skills || []).length > 0 && (
                                                    <div className="mt-1.5 flex flex-wrap gap-1">
                                                        {r.shared_skills.slice(0, 8).map((s, i) => (
                                                            <span key={i} className="text-[10px] px-1.5 py-0.5 rounded bg-[#a78bfa]/12 border border-[#a78bfa]/30 text-[#a78bfa]">{s}</span>
                                                        ))}
                                                    </div>
                                                )}
                                                {r.summary && <p className="mt-1.5 text-[11px] text-[var(--jr-text-3)] line-clamp-2">{r.summary}</p>}
                                            </div>
                                            <button onClick={() => addToPool(r)}
                                                disabled={isAdded || addingUrl === (r.linkedin_url || r.name)}
                                                data-testid={`lookalike-add-${idx}`}
                                                className={`shrink-0 inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full font-medium ${isAdded
                                                    ? "border border-[#22c55e]/40 text-[#22c55e] cursor-default"
                                                    : "bg-[var(--jr-amber)] text-black hover:bg-[var(--jr-amber)]/85"} disabled:opacity-60`}>
                                                {addingUrl === (r.linkedin_url || r.name)
                                                    ? <Loader2 size={12} className="animate-spin" />
                                                    : isAdded ? <Check size={12} /> : <Plus size={12} />}
                                                {isAdded ? i18n.t("lookalike.in_pool") : i18n.t("lookalike.add_btn")}
                                            </button>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
